import { forwardRef } from 'react'
import type { ButtonHTMLAttributes } from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'danger'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  // Fix (visual findings, item 1): was `bg-cc-blue text-hi` — `text-hi` is
  // `--text-heading`, which is dark navy in both Day themes, so this read as
  // near-black lettering on the saturated brand fill instead of the white
  // the design calls for. `text-on-brand` is white in every theme, but
  // white text needs the darker `--brand-primary-strong` fill to clear
  // 4.5:1 (plain `--brand-primary` is only 3.00-4.35:1 across the four
  // themes — see LiveCallStrip.tsx's identical "Fix round 2" comment, the
  // same fix already applied there and to the outbound message bubble).
  primary: 'bg-brand-primary-strong text-on-brand hover:bg-cc-blue-dim',
  // secondary/danger checked for the same mistake: neither pairs a brand/
  // status FILL with `text-hi` or white — `secondary` is `text-hi` on the
  // neutral `bg-elevated` surface (the standard content-on-surface
  // pairing used throughout this console) and `danger` is `text-bad` on
  // the soft `bg-bad-subtle` tint (the standard on-tint pairing) — neither
  // is the "dark heading text on a solid brand/status fill" bug.
  secondary: 'bg-elevated text-hi border border-subtle hover:bg-card',
  danger: 'bg-bad-subtle text-bad border border-subtle hover:bg-card',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', className = '', type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={[
        'inline-flex items-center justify-center gap-1.5 rounded-[7px] px-3 py-1.5',
        'text-[13px] font-medium transition-colors',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        VARIANT_CLASSES[variant],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  )
})
